import i18n from '../../i18n';
import { currencyFormatter } from '../../services/helperService';
import { formatNormalizedUnitPrice, type CountReviewProduct, type InventorySectionProduct } from '../../services/inventory/inventoryService';
import { getCategoryDisplayName } from '../orderhistory/categoryUtils';
import { useInventoryViewStore as useInventoryStore, getInventoryPracticeDataset, isCustomerInventoryPractice } from './practice/inventoryPractice';
import { getPracticeDataset } from './practice/practiceDataset';
import { useCostPriceStore } from '../../stores/appCostPriceStore';
import { useSubrecipesStore } from '../../stores/appSubrecipesStore';
import { useAppUserDefaultStore } from '../../stores/appUserDefaultStore';
import { buildCostPriceInventoryKeys, fmtQuantity } from '../pages/inventory/inventoryReviewShared';
import { groupCountsIntoSessions } from '../pages/inventory/inventoryOverviewUtils';
import { makeLeakageQtyFormat } from '../pages/inventory/leakagePoolDisplay';
import {
  buildVarianceBaselineOptions,
  UNATTRIBUTED_KEY,
  type VarianceRow,
} from '../../utils/reports/inventoryVariance';
import { getTableFormattedDate } from '../../utils/dateUtils';

const WALKTHROUGH_COUNT_TARGET = 1;
const WALKTHROUGH_ADD_COUNT = 2;

type CountSnapshot = {
  productKey: string;
  name: string;
  count: number;
  unit: string;
  value: number;
};

type AddSnapshot = {
  productKey: string;
  name: string;
  price: number;
  unit: string;
};

let countSnapshot: CountSnapshot | null = null;
let addSnapshot: AddSnapshot | null = null;
let sectionSnapshotId: string | null = null;
let setupSnapshot: CountReviewProduct | null = null;

export type LeakageTourSnapshot = {
  expected: number;
  counted: number;
  leakage: number;
  confidenceScore: number;
  linked: number;
  total: number;
  startLabel: string;
  endLabel: string;
  rowUid: string;
  productName: string;
  expectedQtyCount: string;
  countedQtyCount: string;
  leakQtyCount: string;
  leakQtyStd: string;
  leakValue: number;
  leakPct: string;
  startQty: string;
  endQty: string;
  purchasedQty: string;
  neededQty: string;
  varianceQty: string;
  sectionName: string;
  sectionStart: string;
  sectionEnd: string;
  sectionDelta: string;
  top3Names: string;
  cat1Name: string;
  cat1Value: number;
  cat2Name: string;
  cat2Value: number;
  cat3Name: string;
  cat3Value: number;
  sec1Name: string;
  sec1Count: number;
  sec1Value: number;
  sec2Name: string;
  sec3Name: string;
};

let leakageTourState: LeakageTourSnapshot | null = null;

function joinNames(names: string[]): string {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  const conj = i18n.language?.startsWith('nl') ? 'en' : 'and';
  if (names.length === 2) return `${names[0]} ${conj} ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} ${conj} ${names[names.length - 1]}`;
}

function formatMult(ratio: number | null): string {
  if (ratio == null || !Number.isFinite(ratio)) return '';
  const rounded = Math.round(ratio * 10) / 10;
  return `${rounded}×`;
}

function pad2(n: number): string {
  return String(Math.max(0, Math.round(n))).padStart(2, '0');
}

function pct(share: number): string {
  return `${(share * 100).toFixed(1)}%`;
}

function productCatLabel(key: string, mainCategory: string | null): string {
  if (key === 'custom') return i18n.t('inventory.customBucket');
  if (key === 'subrecipe') return i18n.t('inventory.subrecipeBucket');
  if (key === 'uncategorized') return i18n.t('inventory.uncategorizedBucket');
  return getCategoryDisplayName(key, mainCategory ?? undefined) ?? key;
}

/** Live Count Review numbers for Inventory tour copy. */
export function getInventoryTourValues(): Record<string, string> {
  const review = useInventoryStore.getState().countReview;
  const data = review.data;
  const kpis = data?.kpis;
  const counted = kpis?.productsCount ?? 0;
  const unlinked = kpis?.notLinkedCount ?? 0;
  const reorder = kpis?.needsOrderingCount ?? 0;

  const useGroups = review.selection.groupId === null && review.selection.sessionId === null;
  const valueRows = (
    useGroups
      ? (data?.groups ?? []).map(g => ({
          name: g.groupName ?? '',
          value: g.totalValue,
          belowMin: g.belowMinCount,
        }))
      : (data?.sections ?? []).map(s => ({
          name: s.sectionName,
          value: s.totalValue,
          belowMin: s.belowMinCount,
        }))
  ).filter(r => r.name);

  const top = valueRows[0];
  const second = valueRows[1];
  const lower = valueRows[2] ?? valueRows[valueRows.length - 1];

  const invCats = [...(data?.categoriesInventory ?? [])].sort((a, b) => b.share - a.share);
  const prodCats = [...(data?.categoriesProduct ?? [])].sort((a, b) => b.share - a.share);
  const prodLabels = prodCats.map(c => productCatLabel(c.key, c.mainCategory));

  const unlinkedProduct = firstUnlinkedProduct();
  const qty = unlinkedProduct?.quantityDisplay;
  const match = relinkMatchProduct();
  const matchPrice = match ? formatNormalizedUnitPrice(match) : null;
  const notSetup = notSetupProducts();
  const setupProduct = firstNotSetupProduct();
  const setupTarget = setupTargetSubrecipe();
  const setupDish = getPracticeDataset()?.costPriceList.find(dish =>
    dish.ingredients.some(ingredient => ingredient.subrecipeId === setupTarget?.id));
  const setupReference = setupDish?.ingredients.find(ingredient => ingredient.subrecipeId === setupTarget?.id);
  const setupIngredient = setupTarget?.ingredients.find(ingredient => ingredient.productName === setupProduct?.productName);
  const setupUsage = setupTarget && setupTarget.portions > 0
    ? (setupIngredient?.quantityNeeded ?? 0) * (setupReference?.subrecipePortionsNeeded ?? 0) / setupTarget.portions : 0;
  const drill = walkthroughDrillGroup();
  const setupStd = setupProduct?.quantityDisplay?.unit;
  const sectionRows = [...(data?.sections ?? [])]
    .filter(s => s.sectionName)
    .sort((a, b) => b.share - a.share);
  const reviewProduct = firstNeedsReviewProduct();
  const walkSection = walkthroughSection();
  const sectionProducts = (data?.products ?? [])
    .filter(p => walkSection && p.sectionId === walkSection.id)
    .sort((a, b) => b.value - a.value);
  const sectionTop = walkthroughCountProduct() ?? sectionProducts[0] ?? null;
  const sectionOthers = sectionProducts.filter(p => p.productKey !== sectionTop?.productKey);
  const sectionValue = walkSection?.value ?? sectionProducts.reduce((sum, p) => sum + p.value, 0);
  const snap = countSnapshot;
  const countTarget = WALKTHROUGH_COUNT_TARGET;
  const liveCount = sectionTop?.count ?? snap?.count ?? 0;
  const estimatedNewValue =
    snap && snap.count > 0 ? snap.value * (countTarget / snap.count) : (sectionTop?.value ?? 0);

  return {
    ...(isCustomerInventoryPractice() ? { context: 'customer' } : {}),
    counted: String(counted),
    totalValue: currencyFormatter.format(kpis?.totalValue ?? 0),
    unlinked: pad2(unlinked),
    reorder: pad2(reorder),
    topName: top?.name ?? '',
    topValue: currencyFormatter.format(top?.value ?? 0),
    secondName: second?.name ?? '',
    secondValue: currencyFormatter.format(second?.value ?? 0),
    lowerName: lower?.name ?? '',
    lowerValue: currencyFormatter.format(lower?.value ?? 0),
    lowerBelowMin: pad2(lower?.belowMin ?? 0),
    cat1Name: invCats[0]?.name || '',
    cat1Pct: pct(invCats[0]?.share ?? 0),
    cat2Name: invCats[1]?.name || '',
    cat2Pct: pct(invCats[1]?.share ?? 0),
    cat3Name: invCats[2]?.name || '',
    cat3Pct: pct(invCats[2]?.share ?? 0),
    prod1Name: prodLabels[0] ?? '',
    prod1Pct: pct(prodCats[0]?.share ?? 0),
    prod2Name: prodLabels[1] ?? '',
    prod2Pct: pct(prodCats[1]?.share ?? 0),
    restProdNames: prodLabels.slice(2, 6).join(', '),
    unlinkedName: unlinkedProduct?.productName ?? '',
    unlinkedQty: qty ? `${fmtQuantity(qty.value)} ${qty.unit}` : '',
    unlinkedValue: currencyFormatter.format(unlinkedProduct?.value ?? 0),
    unlinkedSection: unlinkedProduct?.sectionName ?? '',
    matchQuery: relinkSearchQuery(),
    matchName: match?.productName ?? '',
    matchPrice: matchPrice ? currencyFormatter.format(matchPrice.value) : '',
    matchUnit: matchPrice?.unit ?? '',
    matchSupplier: match?.platform ?? '',
    notSetup: pad2(notSetup.length),
    notSetupCount: String(notSetup.length),
    setupName: setupProduct?.productName ?? '',
    setupKind: setupProduct?.source === 'subrecipe' ? i18n.t('inventory.subrecipeBucket') : i18n.t('inventory.product'),
    setupTargetName: setupTarget?.name ?? '',
    setupDishName: setupDish?.menuItemName ?? setupDish?.costPriceName ?? '',
    setupUsageQty: `${fmtQuantity(setupUsage)} ${setupIngredient?.quantityInfo?.stdUnit ?? 'g'}`,
    setupItemCount: '1',
    setupQty: setupStd === 'ml' || setupStd === 'L' ? '100 ml' : '100 g',
    drillName: drill?.name ?? '',
    drillValue: currencyFormatter.format(drill?.value ?? 0),
    drillPct: pct(drill?.share ?? 0),
    drillCounted: String(drill?.productCount ?? 0),
    drillUnlinked: pad2(
      review.selection.groupId && (data?.groups ?? []).length === 0 ? (kpis?.notLinkedCount ?? 0) : 0
    ),
    section1Name: sectionRows[0]?.sectionName ?? '',
    section1Pct: pct(sectionRows[0]?.share ?? 0),
    section2Name: sectionRows[1]?.sectionName ?? '',
    section2Pct: pct(sectionRows[1]?.share ?? 0),
    needsReview: pad2(data?.needsReview?.length ?? 0),
    reviewName: reviewProduct?.productName ?? '',
    reviewCounted: reviewProduct
      ? `${pad2(reviewProduct.currentCount)} ${reviewProduct.countUnit}`
      : '',
    reviewUsual: reviewProduct ? `${pad2(reviewProduct.expectedCount)} ${reviewProduct.countUnit}` : '',
    reviewMult: formatMult(reviewProduct?.ratio ?? null),
    reviewImpact: currencyFormatter.format(reviewProduct?.impact ?? 0),
    sectionName: walkSection?.name ?? '',
    sectionPct: pct(walkSection?.share ?? 0),
    sectionValue: currencyFormatter.format(sectionValue),
    sectionCounted: pad2(sectionProducts.length),
    sectionUnlinked: pad2(sectionProducts.filter(p => !p.linked).length),
    sectionMissing: pad2(sectionProducts.filter(p => p.missingPrice).length),
    sectionTopName: sectionTop?.productName ?? '',
    sectionTopValue: currencyFormatter.format(sectionTop?.value ?? 0),
    sectionTopQty: sectionTop ? `${fmtQuantity(sectionTop.count)} ${sectionTop.countUnit}` : '',
    sectionOthersValue: currencyFormatter.format(Math.max(0, sectionValue - (sectionTop?.value ?? 0))),
    sectionOtherNames: joinNames(sectionOthers.slice(0, 2).map(p => p.productName)),
    countFrom: pad2(snap?.count ?? liveCount),
    countTo: pad2(countTarget),
    countToQty: `${fmtQuantity(countTarget)} ${sectionTop?.countUnit ?? snap?.unit ?? ''}`,
    valueFrom: currencyFormatter.format(snap?.value ?? sectionTop?.value ?? 0),
    valueTo: currencyFormatter.format(
      liveCount === countTarget && sectionTop ? sectionTop.value : estimatedNewValue
    ),
    addProductName: walkthroughAddProduct()?.productName ?? addSnapshot?.name ?? '',
    addProductPrice: currencyFormatter.format(walkthroughAddProduct()?.pricePerCountUnit ?? addSnapshot?.price ?? 0),
    addProductUnit: walkthroughAddProduct()?.countUnit ?? addSnapshot?.unit ?? '',
    addCount: String(WALKTHROUGH_ADD_COUNT),
    addCountQty: `${WALKTHROUGH_ADD_COUNT} ${walkthroughAddProduct()?.countUnit ?? addSnapshot?.unit ?? ''}`,
    addProductValue: currencyFormatter.format(
      addedWalkthroughRow()?.value ??
        (walkthroughAddProduct()?.pricePerCountUnit ?? addSnapshot?.price ?? 0) * WALKTHROUGH_ADD_COUNT
    ),
    ...(leakageTourState ? leakageCopyValues() : {}),
  };
}

export function firstNeedsReviewProduct() {
  const list = [...(useInventoryStore.getState().countReview.data?.needsReview ?? [])];
  return list.sort((a, b) => b.impact - a.impact)[0] ?? null;
}

/** Named section the walkthrough steps into — second-highest share when it's substantial. */
export function walkthroughSection() {
  const rows = [...(useInventoryStore.getState().countReview.data?.sections ?? [])]
    .filter(s => s.sectionId && s.sectionName)
    .sort((a, b) => b.share - a.share);
  const pinned = sectionSnapshotId ? rows.find(s => s.sectionId === sectionSnapshotId) : undefined;
  const practiceSectionId = getInventoryPracticeDataset()?.sections[0]?.id;
  const pick = pinned ?? rows.find(row => row.sectionId === practiceSectionId) ?? (rows.length > 1 && rows[1].share >= 0.2 ? rows[1] : rows[0]);
  if (!pick?.sectionId) return null;
  return {
    id: pick.sectionId,
    name: pick.sectionName,
    value: pick.totalValue,
    share: pick.share,
    productCount: pick.productCount,
  };
}

export function resetWalkthroughSnapshots() {
  countSnapshot = null;
  addSnapshot = null;
  sectionSnapshotId = null;
  setupSnapshot = null;
  // LeakageTab owns its report snapshot and clears it on unmount. Clearing it
  // here can erase a report already published by the child's layout effect,
  // without any report-input change that would publish the values again.
}

export function snapshotWalkthroughSection() {
  if (sectionSnapshotId) return;
  const section = walkthroughSection();
  if (section) sectionSnapshotId = section.id;
}

function sectionProductsForWalkthrough(): CountReviewProduct[] {
  const section = walkthroughSection();
  if (!section) return [];
  return (useInventoryStore.getState().countReview.data?.products ?? []).filter(
    p => p.sectionId === section.id && p.countId
  );
}

/** Highest-value counted product in the walkthrough section (stable after a count edit). */
export function walkthroughCountProduct(): CountReviewProduct | null {
  const products = [...sectionProductsForWalkthrough()].sort((a, b) => b.value - a.value);
  if (countSnapshot) {
    const pinned = products.find(p => p.productKey === countSnapshot?.productKey);
    if (pinned) return pinned;
  }
  return products.find(p => p.count !== WALKTHROUGH_COUNT_TARGET) ?? products[0] ?? null;
}

export function walkthroughCountTarget(): number {
  return WALKTHROUGH_COUNT_TARGET;
}

export function snapshotWalkthroughCount() {
  if (countSnapshot) return;
  const product = walkthroughCountProduct();
  if (!product) return;
  countSnapshot = {
    productKey: product.productKey,
    name: product.productName,
    count: product.count,
    unit: product.countUnit,
    value: product.value,
  };
}

/** A product configured in the section but missing from this count, if one exists. */
export function walkthroughAddProduct(): InventorySectionProduct | null {
  const section = walkthroughSection();
  if (!section) return null;
  const counted = new Set(sectionProductsForWalkthrough().map(p => p.productKey));
  const configured = useInventoryStore.getState().sectionProducts[section.id] ?? [];
  if (addSnapshot) {
    return configured.find(p => p.productKey === addSnapshot?.productKey) ?? null;
  }
  const extras = configured.filter(p => !counted.has(p.productKey));
  return (
    extras.find(p => (p.pricePerCountUnit ?? 0) >= 10) ??
    extras.find(p => p.pricePerCountUnit != null && p.pricePerCountUnit > 0) ??
    extras[0] ??
    null
  );
}

export function walkthroughAddProductName(): string {
  return walkthroughAddProduct()?.productName ?? addSnapshot?.name ?? '';
}

export function walkthroughAddCount(): number {
  return WALKTHROUGH_ADD_COUNT;
}

export function snapshotWalkthroughAddProduct() {
  if (addSnapshot) return;
  const product = walkthroughAddProduct();
  if (!product) return;
  addSnapshot = {
    productKey: product.productKey,
    name: product.productName,
    price: product.pricePerCountUnit ?? 0,
    unit: product.countUnit,
  };
}

function addedWalkthroughRow() {
  const key = addSnapshot?.productKey ?? walkthroughAddProduct()?.productKey;
  if (!key) return null;
  const section = walkthroughSection();
  return (
    (useInventoryStore.getState().countReview.data?.products ?? []).find(
      p => p.productKey === key && (!section || p.sectionId === section.id)
    ) ?? null
  );
}

export function walkthroughAddedProductKey(): string | null {
  return addSnapshot?.productKey ?? addedWalkthroughRow()?.productKey ?? null;
}

export function walkthroughAddAlreadyCounted(): boolean {
  return addedWalkthroughRow() != null;
}

export function firstUnlinkedProduct() {
  return useInventoryStore.getState().countReview.data?.products.find(p => !p.linked) ?? null;
}

function nameTokens(name: string): string[] {
  return name
    .split(/[\s/]+/)
    .map(t => t.replace(/^[^a-zA-Z0-9]+|[^a-zA-Z0-9]+$/g, ''))
    .filter(t => t.replace(/[^a-zA-Z0-9]/g, '').length >= 3);
}

/** Order-history product the walkthrough should link the unlinked count to. */
export function relinkMatchProduct() {
  const products = useInventoryStore.getState().availableProducts;
  if (products.length === 0) return null;
  const tokens = nameTokens(firstUnlinkedProduct()?.productName ?? '');
  for (const token of [...tokens].reverse()) {
    const found = products.find(p => p.productName.toLowerCase().includes(token.toLowerCase()));
    if (found) return found;
  }
  return products[0] ?? null;
}

/** Search token that keeps the matching order product in the filtered list. */
export function relinkSearchQuery(): string {
  const match = relinkMatchProduct();
  if (!match) return '';
  const unlinkedTokens = nameTokens(firstUnlinkedProduct()?.productName ?? '');
  for (const token of [...unlinkedTokens].reverse()) {
    if (match.productName.toLowerCase().includes(token.toLowerCase())) return token;
  }
  return nameTokens(match.productName)[0] ?? match.productName;
}

export function notSetupProducts() {
  const data = useInventoryStore.getState().countReview.data;
  const restaurantId = useAppUserDefaultStore.getState().restaurant?.id;
  const cost = useCostPriceStore.getState();
  const subs = useSubrecipesStore.getState();
  if (!getPracticeDataset() && (!restaurantId || cost.currentRestaurantId !== restaurantId || subs.currentRestaurantId !== restaurantId)) {
    return [];
  }
  const keys = buildCostPriceInventoryKeys(getPracticeDataset()?.costPriceList ?? cost.costPriceList, getPracticeDataset()?.subrecipesById ?? subs.subrecipesById);
  return (data?.products ?? []).filter(p => p.linked && !keys.has(p.productKey));
}

export function firstNotSetupProduct() {
  const list = notSetupProducts();
  setupSnapshot = [...list].sort((a, b) => b.value - a.value)[0] ?? setupSnapshot;
  return setupSnapshot;
}

export function setupTargetSubrecipe() {
  const subject = firstNotSetupProduct();
  const subjectId =
    subject?.source === 'subrecipe' && subject.productKey.startsWith('subrecipe:')
      ? subject.productKey.replace(/__v\d+$/, '').slice('subrecipe:'.length)
      : null;
  return (
    Array.from((getPracticeDataset()?.subrecipesById ?? useSubrecipesStore.getState().subrecipesById).values()).find(s => s.id !== subjectId)
    ?? null
  );
}

/** Highest-value inventory group the walkthrough drills into. */
export function walkthroughDrillGroup() {
  const review = useInventoryStore.getState().countReview;
  const data = review.data;
  const rows = [...(data?.groups ?? [])]
    .filter(g => g.groupId && g.groupName)
    .sort((a, b) => b.share - a.share);
  const selectedId = review.selection.groupId;
  const selected = rows.find(g => g.groupId === selectedId);
  const top = selected ?? rows[0];
  if (top?.groupId && top.groupName) {
    return {
      id: top.groupId,
      name: top.groupName,
      value: top.totalValue,
      share: top.share,
      productCount: top.productCount,
    };
  }
  if (!selectedId) return null;
  const named = useInventoryStore.getState().groups.find(g => g.id === selectedId);
  if (!named) return null;
  return {
    id: named.id,
    name: named.name,
    value: data?.kpis?.totalValue ?? 0,
    share: 1,
    productCount: data?.kpis?.productsCount ?? 0,
  };
}

function moneyAbs(n: number): string {
  return currencyFormatter.format(Math.abs(n));
}

function leakageRowUid(row: VarianceRow): string {
  return `${(row as VarianceRow & { groupName?: string | null }).groupName ?? ''}|${row.key}`;
}

function sortedLeakageRows(rows: VarianceRow[]): VarianceRow[] {
  return [...rows].sort((a, b) => (a.varianceValue ?? Infinity) - (b.varianceValue ?? Infinity));
}

export function leakageWalkthroughRowUid(): string | null {
  return leakageTourState?.rowUid || null;
}
export const leakageHasNegativeRows = () => Boolean(leakageTourState?.top3Names);
export const leakageHasSection = () => Boolean(leakageTourState?.sectionName);

let leakageValuesRevision = 0;
const leakageValuesListeners = new Set<() => void>();
export const leakageTourRevision = () => leakageValuesRevision;
export const subscribeLeakageTourValues = (listener: () => void) => {
  leakageValuesListeners.add(listener);
  return () => { leakageValuesListeners.delete(listener); };
};

export function publishLeakageTourState(next: LeakageTourSnapshot | null) {
  const prev = leakageTourState;
  if (prev && next && JSON.stringify(prev) === JSON.stringify(next)) return;
  if (!prev && !next) return;
  leakageTourState = next;
  leakageValuesRevision++;
  leakageValuesListeners.forEach(listener => listener());
}

export function buildLeakageTourSnapshot(input: {
  expected: number;
  counted: number;
  leakage: number;
  confidenceScore: number;
  linked: number;
  total: number;
  startLabel: string;
  endLabel: string;
  rows: VarianceRow[];
}): LeakageTourSnapshot {
  const countFmt = makeLeakageQtyFormat('count');
  const stdFmt = makeLeakageQtyFormat('std');
  const ranked = sortedLeakageRows(input.rows);
  const pinnedUid = leakageTourState?.rowUid;
  const pinned = pinnedUid ? ranked.find(r => leakageRowUid(r) === pinnedUid) : undefined;
  const product = pinned ?? ranked[0];
  const negatives = ranked.filter(r => r.varianceValue != null && r.varianceValue < 0);
  const section = product
    ? [...(product.sectionChanges ?? [])].sort((a, b) => Math.abs(b.deltaQty) - Math.abs(a.deltaQty))[0]
    : undefined;
  const catsAll = rankedLeakageCategories(input.rows);
  const sectionsAll = rankedLeakageSections(input.rows);
  const cats = catsAll.some(c => c.value < 0) ? catsAll.filter(c => c.value < 0) : catsAll;
  const sections = sectionsAll.some(s => s.value < 0) ? sectionsAll.filter(s => s.value < 0) : sectionsAll;
  return {
    expected: input.expected,
    counted: input.counted,
    leakage: input.leakage,
    confidenceScore: input.confidenceScore,
    linked: input.linked,
    total: input.total,
    startLabel: input.startLabel,
    endLabel: input.endLabel,
    rowUid: product ? leakageRowUid(product) : '',
    productName: product?.productName ?? '',
    expectedQtyCount: product ? countFmt.row(product, product.expectedQty) : '',
    countedQtyCount: product ? countFmt.row(product, product.endQty) : '',
    leakQtyCount: product ? countFmt.row(product, product.varianceQty) : '',
    leakQtyStd: product ? stdFmt.row(product, product.varianceQty) : '',
    leakValue: product?.varianceValue ?? 0,
    leakPct: product?.variancePct != null ? `${Math.abs(Math.round(product.variancePct * 100))}%` : '',
    startQty: product ? stdFmt.row(product, product.startQty) : '',
    endQty: product ? stdFmt.row(product, product.endQty) : '',
    purchasedQty: product ? stdFmt.row(product, product.purchasedQty) : '',
    neededQty: product ? stdFmt.row(product, product.neededQty) : '',
    varianceQty: product ? stdFmt.row(product, product.varianceQty) : '',
    sectionName: section?.sectionName ?? '',
    sectionStart: product && section ? stdFmt.row(product, section.startQty) : '',
    sectionEnd: product && section ? stdFmt.row(product, section.endQty) : '',
    sectionDelta: product && section ? stdFmt.row(product, section.deltaQty) : '',
    top3Names: joinNames(negatives.slice(0, 3).map(r => r.productName)),
    cat1Name: cats[0]?.name ?? '',
    cat1Value: cats[0]?.value ?? 0,
    cat2Name: cats[1]?.name ?? '',
    cat2Value: cats[1]?.value ?? 0,
    cat3Name: cats[2]?.name ?? '',
    cat3Value: cats[2]?.value ?? 0,
    sec1Name: sections[0]?.name ?? '',
    sec1Count: sections[0]?.count ?? 0,
    sec1Value: sections[0]?.value ?? 0,
    sec2Name: sections[1]?.name ?? '',
    sec3Name: sections[2]?.name ?? '',
  };
}

function rankedLeakageCategories(rows: VarianceRow[]): { name: string; value: number }[] {
  const uncategorized = i18n.t('inventory.uncategorizedBucket');
  const map = new Map<string, number>();
  for (const row of rows) {
    const name = row.inventoryCategoryName?.trim() || uncategorized;
    map.set(name, (map.get(name) ?? 0) + (row.varianceValue ?? 0));
  }
  return Array.from(map.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
}

function rankedLeakageSections(rows: VarianceRow[]): { name: string; count: number; value: number }[] {
  const unattributed = i18n.t('inventory.leakage.unattributed');
  const map = new Map<string, { count: number; value: number }>();
  for (const row of rows) {
    const name =
      row.attributedSectionKey === UNATTRIBUTED_KEY || !row.attributedSectionName?.trim()
        ? unattributed
        : row.attributedSectionName.trim();
    const cur = map.get(name) ?? { count: 0, value: 0 };
    cur.count += 1;
    cur.value += row.varianceValue ?? 0;
    map.set(name, cur);
  }
  return Array.from(map.entries())
    .map(([name, v]) => ({ name, ...v }))
    .sort((a, b) => Math.abs(b.value) - Math.abs(a.value));
}

function aroundMoney(n: number): string {
  const mag = Math.abs(n);
  const rounded = mag >= 50 ? Math.round(mag / 10) * 10 : Math.round(mag);
  return currencyFormatter.format(rounded);
}

export function leakageWalkthroughProductName(): string {
  return leakageTourState?.productName ?? '';
}

function productNamesMatch(a: string, b: string): boolean {
  const x = a.trim().toLowerCase();
  const y = b.trim().toLowerCase();
  if (!x || !y) return false;
  return x === y || x.includes(y) || y.includes(x);
}

function leakageIngredientNums(): { from: number; to: number; unit: string } {
  const dish = leakageSetupDish();
  const product = leakageTourState?.productName ?? '';
  const analysis = (getPracticeDataset()?.costPriceList ?? useCostPriceStore.getState().costPriceList).find(a => {
      const name = (a.menuItemName || a.costPriceName || '').trim();
      return name && dish && name.toLowerCase() === dish.toLowerCase();
    });
  const ingredient = analysis?.ingredients?.find(i => productNamesMatch(i.productName ?? '', product));
  const unit = ingredient?.quantityInfo?.stdUnit || 'g';
  const from = Number(ingredient?.quantityNeeded) || (unit === 'g' || unit === 'ml' ? 100 : 1);
  const to = unit === 'g' || unit === 'ml' ? 50 : Math.max(1, Math.round(from / 2));
  return { from, to, unit };
}

function fmtIngQty(qty: number, unit: string): string {
  const shown = Number.isInteger(qty) ? String(qty) : String(qty);
  return `${shown} ${unit}`;
}

export function leakageIngredientTarget(): { name: string; qty: string } {
  return {
    name: leakageTourState?.productName ?? '',
    qty: String(leakageIngredientNums().to),
  };
}

function leakageGroupSessions() {
  const group = walkthroughDrillGroup();
  const sessions = groupCountsIntoSessions(useInventoryStore.getState().countsHistory);
  if (!group) return sessions.filter(s => s.groupId);
  return sessions.filter(s => s.groupId === group.id);
}

/**
 * A different current count than Latest, only when that session still has an older
 * baseline (needs ≥3 sessions). Otherwise the pair step changes Compared against.
 */
export function walkthroughLeakageAltSession() {
  const sessions = leakageGroupSessions();
  if (sessions.length < 3) return null;
  return sessions[1] ?? null;
}

export function walkthroughLeakageAltSessionId(): string | null {
  return walkthroughLeakageAltSession()?.counts[0]?.sessionId ?? null;
}

export function walkthroughLeakageAltBaselineKey(): string | null {
  const group = walkthroughDrillGroup();
  const latest = leakageGroupSessions()[0];
  if (!group || !latest) return null;
  const options = buildVarianceBaselineOptions(
    useInventoryStore.getState().countsHistory,
    group.id,
    latest.key
  );
  return options[1]?.key ?? null;
}

export function walkthroughLeakageAltDates(): { current: string; previous: string } {
  const alt = walkthroughLeakageAltSession();
  const group = walkthroughDrillGroup();
  if (alt && group) {
    const baselines = buildVarianceBaselineOptions(
      useInventoryStore.getState().countsHistory,
      group.id,
      alt.key
    );
    return {
      current: getTableFormattedDate(alt.completedAt),
      previous: baselines[0]?.label ?? '',
    };
  }
  const latest = leakageGroupSessions()[0];
  const baselineKey = walkthroughLeakageAltBaselineKey();
  if (latest && group && baselineKey) {
    const options = buildVarianceBaselineOptions(
      useInventoryStore.getState().countsHistory,
      group.id,
      latest.key
    );
    const pick = options.find(o => o.key === baselineKey);
    return {
      current: getTableFormattedDate(latest.completedAt),
      previous: pick?.label ?? '',
    };
  }
  return {
    current: leakageTourState?.endLabel ?? '',
    previous: leakageTourState?.startLabel ?? '',
  };
}

export function leakageSetupDish(): string {
  const names = (getPracticeDataset()?.costPriceList ?? useCostPriceStore.getState().costPriceList).map(a => (a.menuItemName || a.costPriceName || '').trim())
    .filter(Boolean);
  return names.find(n => nameTokens(n).length > 0) ?? names[0] ?? '';
}

export function leakageSetupQuery(): string {
  const name = leakageSetupDish();
  return nameTokens(name)[0] ?? name;
}

function leakageCopyValues(): Record<string, string> {
  const s = leakageTourState;
  const alt = walkthroughLeakageAltDates();
  const dish = leakageSetupDish();
  const ing = leakageIngredientNums();
  return {
    leakExpected: currencyFormatter.format(s?.expected ?? 0),
    leakCounted: currencyFormatter.format(s?.counted ?? 0),
    leakTotal: moneyAbs(s?.leakage ?? 0),
    leakSignedTotal: currencyFormatter.format(s?.leakage ?? 0),
    leakConfidence: `${Math.round((s?.confidenceScore ?? 0) * 100)}%`,
    leakLinked: pad2(s?.linked ?? 0),
    leakLinkedTotal: pad2(s?.total ?? 0),
    leakStartDate: s?.startLabel ?? '',
    leakEndDate: s?.endLabel ?? '',
    leakAltCurrent: alt.current,
    leakAltPrevious: alt.previous,
    leakProduct: s?.productName ?? '',
    leakExpectedQty: s?.expectedQtyCount ?? '',
    leakCountedQty: s?.countedQtyCount ?? '',
    leakProductQtyCount: s?.leakQtyCount ?? '',
    leakProductQtyStd: s?.leakQtyStd ?? '',
    leakProductValue: moneyAbs(s?.leakValue ?? 0),
    leakSignedProductValue: currencyFormatter.format(s?.leakValue ?? 0),
    leakProductPct: s?.leakPct ?? '',
    leakTop3: s?.top3Names ?? '',
    leakOpenQty: s?.startQty ?? '',
    leakCloseQty: s?.endQty ?? '',
    leakPurchasedQty: s?.purchasedQty ?? '',
    leakNeededQty: s?.neededQty ?? '',
    leakVarianceQty: s?.varianceQty ?? '',
    leakSection: s?.sectionName ?? '',
    leakSectionStart: s?.sectionStart ?? '',
    leakSectionEnd: s?.sectionEnd ?? '',
    leakSectionDelta: s?.sectionDelta ?? '',
    leakDish: dish,
    leakDishQuery: leakageSetupQuery(),
    leakIngFrom: fmtIngQty(ing.from, ing.unit),
    leakIngTo: fmtIngQty(ing.to, ing.unit),
    cat1Name: s?.cat1Name ?? '',
    cat1Value: aroundMoney(s?.cat1Value ?? 0),
    cat2Name: s?.cat2Name ?? '',
    cat2Value: aroundMoney(s?.cat2Value ?? 0),
    cat3Name: s?.cat3Name ?? '',
    cat3Value: aroundMoney(s?.cat3Value ?? 0),
    catLead: i18n.t(
      `onboarding.inventory.leakageCategories.${s?.cat3Name ? 'lead3' : s?.cat2Name ? 'lead2' : 'lead1'}`,
      {
        cat1Name: s?.cat1Name ?? '',
        cat1Value: aroundMoney(s?.cat1Value ?? 0),
        cat2Name: s?.cat2Name ?? '',
        cat2Value: aroundMoney(s?.cat2Value ?? 0),
        cat3Name: s?.cat3Name ?? '',
        cat3Value: aroundMoney(s?.cat3Value ?? 0),
      }
    ),
    sec1Name: s?.sec1Name ?? '',
    sec1Count: pad2(s?.sec1Count ?? 0),
    sec1CountLabel: (s?.sec1Count ?? 0) === 1
      ? 'product'
      : (i18n.language?.startsWith('nl') ? 'producten' : 'products'),
    sec1Value: aroundMoney(s?.sec1Value ?? 0),
    sec2Name: s?.sec2Name ?? '',
    sec3Name: s?.sec3Name ?? '',
    secLead: i18n.t(
      `onboarding.inventory.leakageSections.${s?.sec3Name ? 'lead3' : s?.sec2Name ? 'lead2' : 'lead1'}`,
      {
        sec1Count: pad2(s?.sec1Count ?? 0),
        sec1CountLabel: (s?.sec1Count ?? 0) === 1
          ? 'product'
          : (i18n.language?.startsWith('nl') ? 'producten' : 'products'),
        sec1Name: s?.sec1Name ?? '',
        sec1Value: aroundMoney(s?.sec1Value ?? 0),
        sec2Name: s?.sec2Name ?? '',
        sec3Name: s?.sec3Name ?? '',
      }
    ),
  };
}
